import { useState, useEffect, useRef } from "react";
import Chart from 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-moment';

function GraphData({ viewData, historical, res, xInterval, graphType }) {
  Chart.register(zoomPlugin);

  const [chartData, setChartData] = useState();
  const [chartOptions, setChartOptions] = useState({});
  const [minChartWidth, setMinChartWidth] = useState();
  const [xRange, setXRange] = useState({});
  const graphRef = useRef(null);


  const [xMin, setXMin] = useState(0);
  const [xMax, setXMax] = useState(100);

  const MIN_TICK_PIXEL = 40;
  const MAX_CANVAS_WIDTH = 20000;

  const generateAlignedTicks = () => {
    if (!viewData?.labels?.length || !xInterval) return [];

    const intervalValue = Number(xInterval.slice(0, -1));
    const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";

    const start = new Date(viewData.labels[0]);
    const end = new Date(viewData.labels.at(-1));

    const ticks = [];
    const current = new Date(start);

    // 🔥 ALIGN TO INTERVAL
    if (unit === "minute") {
      current.setSeconds(0, 0);
      current.setMinutes(Math.floor(current.getMinutes() / intervalValue) * intervalValue);
    } else {
      current.setMinutes(0, 0, 0);
      current.setHours(Math.floor(current.getHours() / intervalValue) * intervalValue);
    }

    while (current <= end) {
      ticks.push(new Date(current));
      unit === "hour"
        ? current.setHours(current.getHours() + intervalValue)
        : current.setMinutes(current.getMinutes() + intervalValue);
    }

    return ticks;
  };



  const calculateTickCount = () => {

    if (!viewData?.labels?.length || !xInterval) return 0;

    const intervalValue = Number(xInterval.slice(0, -1));
    const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";

    const start = new Date(viewData.labels[0]).getTime();
    const end = new Date(viewData.labels.at(-1)).getTime();

    const diffMs = end - start;
    const intervalMs = unit === "hour"
      ? intervalValue * 3600000
      : intervalValue * 60000;

    return Math.ceil(diffMs / intervalMs);
  };

  useEffect(() => {

    setXRange({
      min: viewData?.labels[0],
      max: viewData?.labels[viewData?.labels?.length - 1],
    });


    const filteredDatasets = viewData?.datasets?.filter((dataset) => !dataset.label.includes("Event_"));

    let copiedViewData = { ...viewData };

    copiedViewData.datasets = filteredDatasets;

    setChartData(copiedViewData);



  }, [viewData])


  useEffect(() => {
    if (!historical || !xInterval || !viewData?.labels?.length) {
      setMinChartWidth("100%");
      return;
    }

    const ticks = calculateTickCount();
    const requiredWidth = ticks * MIN_TICK_PIXEL;

    if (requiredWidth <= MAX_CANVAS_WIDTH) {
      setMinChartWidth(`${requiredWidth}px`);
    } else {
      // ⚠️ width limit reached → expand visible time window instead
      const ratio = requiredWidth / MAX_CANVAS_WIDTH;

      const intervalValue = Number(xInterval.slice(0, -1));
      const unitChar = xInterval.slice(-1).toUpperCase();

      const newInterval =
        unitChar === "H"
          ? `${Math.ceil(intervalValue * ratio)}H`
          : `${Math.ceil(intervalValue * ratio)}M`;

      setXRange({
        min: viewData.labels[0],
        max: viewData.labels.at(-1),
      });

      setMinChartWidth(`${MAX_CANVAS_WIDTH}px`);
    }
  }, [historical, xInterval, viewData]);

  const getXScale = () => {
    if (!historical || !xInterval) return { type: "time" };

    const ticks = generateAlignedTicks();

    return {
      type: "time",
      min: ticks[0],
      max: ticks.at(-1),

      afterBuildTicks: scale => {
        scale.ticks = ticks.map(t => ({ value: t.getTime() }));
      },

      ticks: {
        autoSkip: false,
        callback: function (value, index, ticksArr) {
          const dt = new Date(ticksArr[index].value);
          const unit = xInterval.slice(-1).toUpperCase();

          if (unit === "M") {
            return dt.toLocaleString([], {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit"
            });
          }

          if (unit === "H") {
            return dt.toLocaleString([], {
              day: "2-digit",
              month: "short",
              hour: "2-digit"
            });
          }

          return dt.toLocaleDateString([], {
            day: "2-digit",
            month: "short"
          });
        }
      }
    };
  };
  /* ---------------- OPTIONS ---------------- */
  useEffect(() => {
    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: getXScale(),
        y: {
          suggestedMin: xMin,
          suggestedMax: xMax,
        },
      },
      plugins: {
        decimation: {
          enabled: true,
          algorithm: "lttb",
          samples: res ?? 1,
          threshold: 200,
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "y",
            modifierKey: "ctrl",
          },
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            mode: "y",
          },
        },
        legend: {
          align: "left",
          labels: {
            boxWidth: 5,
            font: { size: 10 },
          },
        },
      },
    });
  }, [historical, xInterval, res, minChartWidth, xRange]);



  return (
    <div
      style={{
        width: "100%",
        height: "370px"
      }}
    >
      {/* SCROLL CONTAINER */}
      <div
        style={{
          width: "100%",
          height: "100%",
          overflowX: historical && xInterval ? "auto" : "hidden",
          overflowY: "hidden"
        }}
      >
        {/* GROWING CONTAINER */}
        <div
          style={{
            minWidth: historical ? minChartWidth : "100%",
            height: "100%"
          }}
        >
          {chartData && chartData?.labels ? (
            <Line
              ref={graphRef}
              data={chartData}
              options={chartOptions}
            />
          ) : (
            <p>Loading chart data...</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default GraphData;